<template>
    <div class="card mt-3">
        <div class="card-header pb-3">
            <div class="d-flex justify-content-between align-items-center">
                <div>
                    <span class="h4">{{ $t('users.title') }}</span>
                </div>
            </div>
        </div>
        <div class="card-body pt-0">
            <div class="mb-4">
                <input
                    type="text"
                    placeholder="Zoeken..."
                    class="form-control"
                    v-model="search"
                    @keydown.enter="loadUsers(1)"
                    @blur="loadUsers(1)"
                />
            </div>

            <table class="table table-sm table-no-side-paddings table-striped">
                <thead>
                    <tr>
                        <Ordering @change="loadUsers" v-model="order" field="name" tag="th">
                            {{ $t('users.name') }}
                        </Ordering>
                        <Ordering @change="loadUsers" v-model="order" field="email" tag="th">
                            {{ $t('users.email') }}
                        </Ordering>
                        <th>
                            {{ $t('users.company') }}
                        </th>
                        <th>
                            Aangemaakte plechtigheden
                        </th>
                        <th>
                            {{ $t('users.role') }}
                        </th>
                        <Ordering
                            @change="loadUsers"
                            v-model="order"
                            field="created_at"
                            class="text-right"
                            tag="th"
                        >
                            Aangemaakt op
                        </Ordering>
                        <th />
                    </tr>
                </thead>

                <tbody v-if="users?.data?.length">
                    <tr v-for="user in users.data" :key="user.id">
                        <td>{{ user.name }}</td>
                        <td>{{ user.email }}</td>
                        <td>
                            <span v-if="user.company">{{ user.company.name }} ({{ user.company.id }})</span>
                            <span v-else>-</span>
                        </td>
                        <td>{{ user.ceremonies_count || '-' }}</td>
                        <td>{{ translateRoles(user.roles).join(', ') }}</td>
                        <td class="text-right">{{ formatDateTime(user.created_at) }}</td>
                        <td class="text-right">
                            <button
                                v-if="canImpersonate(user) && hasPermission('adminEditUser')"
                                class="btn btn-xs rounded-pill btn-warning"
                                @click="impersonate(user)"
                                v-tooltip="'Account overnemen'"
                            >
                                <icon name="user-secret" scale="0.8"></icon>
                            </button>

                            <button v-if="hasPermission('adminEditUser')"
                                    class="btn btn-xs rounded-pill btn-primary"
                                    @click="userToEdit = user"
                                    :v-tooltip="$t('general.edit')">
                                <icon name="pencil-alt" scale="0.8"></icon>
                            </button>

                            <button v-if="hasPermission('adminDeleteUser')"
                                    class="btn btn-xs rounded-pill btn-danger"
                                    @click="userToDelete = user"
                                    :disabled="store.state.user.id === user.id"
                                    :v-tooltip="$t('general.delete')">
                                <icon name="trash" scale="0.8"></icon>
                            </button>
                        </td>
                    </tr>
                </tbody>

                <tbody v-if="!isLoading && !users?.data?.length">
                    <tr>
                        <td colspan="100%">
                            {{ $t('users.no_users_found') }}
                        </td>
                    </tr>
                </tbody>
            </table>

            <Pagination v-if="users" :pagination="users" @change="loadUsers" />

            <EditUserModal v-if="userToEdit" @updated="loadUsers(); userToEdit = null" :user="userToEdit" :admin="true" @close="userToEdit = null" />
            <DeleteUserModal v-if="userToDelete" @deleted="loadUsers(); userToDelete = null" :user="userToDelete" :admin="true" @close="userToDelete = null" />
        </div>
    </div>
</template>

<script setup>
    import { translateRoles } from '../../../utils/auth';
    import Pagination from '../../../components/Pagination.vue';
    import EditUserModal from '../../../components/users/EditUserModal.vue';
    import Ordering from '../../../components/Ordering.vue';
    import DeleteUserModal from '../../../components/users/DeleteUserModal.vue';
    import { inject, onMounted, ref } from 'vue';
    import { notifyError } from '../../../utils/notify';
    import { formatDateTime } from '../../../utils/formatDate';

    const store = inject('store');
    const http = inject('http');

    const hasPermission = store.getters.hasPermission;

    const isLoading = ref(false);
    const order = ref('created_at,desc');
    const search = ref(null);
    const users = ref(null);

    const userToDelete = ref(null);
    const userToEdit = ref(null);

    onMounted(() => {
        const params = new URLSearchParams(window.location.search);

        order.value = params.get('order') || 'created_at,desc';
        search.value = params.get('search');

        loadUsers(params.get('page'));
    });

    async function loadUsers(pageNumber = null) {
        isLoading.value = true;

        try {
            const page = pageNumber || users.value?.current_page || 1;

            const params = new URLSearchParams();

            params.append('order', order.value);
            params.append('page', page);
            if (search.value?.length) {
                params.append('search', search.value);
            }

            const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
            window.history.pushState({}, '', newUrl);

            const response = await http.get('/api/admin/users', {
                params: {
                    page,
                    search: search.value,
                    order: order.value,
                },
            });

            users.value = response.data;
        } catch (exception) {
            notifyError(exception);
        } finally {
            isLoading.value = false;
        }
    }

    function canImpersonate(user) {
        if (!user.company) {
            return false;
        }

        if (store.state.user.id === user.id) {
            return false;
        }

        return !user.roles.some(role => role.name === 'admin');
    }

    async function impersonate(user) {
        isLoading.value = true;

        try {
            const response = await http.post('/api/admin/users/' + user.id + '/impersonate');

            window.location = response.data.redirect_url;

            return;
        } catch (exception) {
            notifyError(exception, exception.response?.data?.message);
        }

        isLoading.value = false;
    }
</script>
